import {SpectrumYAxisType} from '@/enums/diagrams';
/*
    Провайдер для работы с осями графиков
*/

import {ComparedDiagramData, DiagramData} from '@/interfaces/diagrams';
import {AxisColorScheme} from '@/utils/colorSchemes';
import {replaceCaret} from '@/utils/unitUtils';

export class DiagramAxisProvider {
  // Получить список осей Y
  getYAxis(
    data: ComparedDiagramData,
    colorSchemeList: AxisColorScheme[],
    isLogSetting: boolean,
    spectrumYAxisType: {name: string; value: SpectrumYAxisType} | null = null,
    isDiagramToCompare = false,
  ): {axis: any[]; comparedSeriesAxisNumbers: number[]; comparedSeriesColors: string[]} {
    //Группируем оси по units

    let yTitle = data.mainDiagram.yAxis.name;
    if (spectrumYAxisType) yTitle = spectrumYAxisType.name;

    const mainAxis = {
      axis: {
        title: {text: yTitle, useHTML: true},
        labels: {
          useHTML: true,
          formatter: this.yDefaultFormatter,
        },
        id: 0,
      },
      units: data.mainDiagram.yAxis.units,
      index: 0,
      seriesCount: 1,
    };

    //TODO: Это нужно исправить!!!
    const isDb = data.mainDiagram.yAxis.units === 'дБ';
    const isLog = isLogSetting && !isDb;

    this.setupAxisFields(isLog, mainAxis, data.mainDiagram.data);
    const distinctAxis: {axis: any; units: string; index: number; seriesCount: number}[] = [
      mainAxis,
    ];

    const comparedSeriesAxisNumbers: number[] = [];
    let comparedSeriesColors: string[] = [];

    data.comparedDiagrams.forEach((d: DiagramData) => {
      let axis = distinctAxis.find(x => x.units === d.yAxis.units);
      let colorScheme: AxisColorScheme | null = null;
      if (!axis) {
        colorScheme = colorSchemeList[distinctAxis.length];
        axis = {
          axis: {
            title: {
              useHTML: true,
              text: d.yAxis.name,
              style: {
                color: '#000', //colorScheme.mainColor,
              },
            },
            labels: {
              useHTML: true,
              format: '{value}',
              style: {
                color: '#000', // colorScheme.mainColor,
              },
            },
            opposite: true,
            id: distinctAxis.length,
          },
          units: d.yAxis.units,
          index: distinctAxis.length,
          seriesCount: 1,
        };

        const isDb = d.yAxis.units === 'дБ';
        const isLog = isLogSetting && !isDb;
        this.setupAxisFields(isLog, axis, d.data);
        distinctAxis.push(axis);
      } else {
        colorScheme = colorSchemeList[axis.index];
        axis.seriesCount++;
      }
      comparedSeriesAxisNumbers.push(axis.index);
      comparedSeriesColors.push(
        colorScheme.comparedColors[(axis.seriesCount - 1) % colorScheme.comparedColors.length],
      );
    });

    if (distinctAxis.length === 1) {
      const distinctColors: string[] = [];
      comparedSeriesColors.forEach((c: string, index: number) =>
        distinctColors.push(colorSchemeList[(index + 1) % colorSchemeList.length].mainColor),
      );
      if (!isDiagramToCompare) comparedSeriesColors = distinctColors;
    }

    return {axis: distinctAxis.map(x => x.axis), comparedSeriesAxisNumbers, comparedSeriesColors};
  }

  yDefaultFormatter(e: any) {
    return e.value;
  }

  yLogFormatter(e: any) {
    const degree = Math.log10(e.value);
    return [0, 1].includes(degree) || Math.round(degree) !== degree
      ? e.value
      : replaceCaret('10', `${degree}`);
  }

  private setupAxisFields(isLog: boolean, a: any, data: any[]) {
    if (isLog) this.setupLogAxis(a, data);
    else this.setupLinearAxis(a);
  }

  private setupLogAxis(a: any, data: any[]) {
    const positiveYValues = data
      .map(p => p.y)
      .filter(y => y > 0)
      .concat([1]);

    const minY = Math.min(...positiveYValues);
    const maxY = Math.max(...positiveYValues);

    const minPower = Math.floor(Math.log10(minY)) - 1;
    const maxPower = Math.ceil(Math.log10(maxY)) + 1;

    a.axis.type = 'logarithmic';
    a.axis.minorGridLineColor = '#BDBDBD';
    a.axis.gridLineColor = '#757575';
    a.axis.minorGridLineDashStyle = 'longdash';
    a.axis.gridLineWidth = 0;

    //Без атрибута minRange не работает зум по Y на логарифмической шкале
    a.axis.minRange = 0.01;

    const plotLines = [];
    const values: any[] = [];
    const base = 10;

    const minorSegmentsNumber = 10;
    for (let i = minPower; i <= maxPower; ++i) {
      const p = Math.pow(base, i);
      const np = Math.pow(base, i + 1);
      const delta = (np - p) / minorSegmentsNumber;
      for (let j = p + delta; j <= np; j += delta) {
        plotLines.push({
          value: j,
          width: 1,
          color: '#BDBDBD',
          dashStyle: 'longdash',
          zIndex: 0,
        });
      }
      plotLines.push({
        value: p,
        width: 1,
        color: '#9E9E9E',
        zIndex: 0,
      });
      values.push(p);
    }

    a.axis.plotLines = plotLines;
    a.axis.labels.formatter = (e: any) => {
      if (values.includes(e.value)) return this.yLogFormatter(e);
      return '';
    };
  }

  private setupLinearAxis(a: any) {
    a.axis.type = 'linear';
    a.axis.gridLineColor = '#e6e6e6';
    a.axis.gridLineWidth = 1;
    a.axis.minTickInterval = undefined;
    a.axis.labels.formatter = this.yDefaultFormatter;
    a.axis.plotLines = [];
  }
}
